/**
 * Iter208.33 — Vista Calendario del Professionista.
 *
 * Aggrega tutti gli appuntamenti del PRO (personal + shared) su tutti i
 * suoi immobili + i sopralluoghi delle pratiche (read-only).
 *
 * - Vista mensile stile Google Calendar (7×6).
 * - "+ Nuovo": chiede l'immobile → tipo (personal/shared) → apre il form esistente.
 * - Click su cella con eventi → espande gli eventi del giorno sotto la griglia.
 * - Click su un evento → dettaglio (naviga all'immobile o alla pratica).
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  CaretLeft, CaretRight, Plus, CalendarBlank, MapPin, User,
  Buildings, Wrench, ArrowRight, Clock, List as ListIcon,
  WhatsappLogo, PencilSimple, Trash, BellRinging,
} from "@phosphor-icons/react";
import AppointmentTypeChooserModal from "@/components/AppointmentTypeChooserModal";
import QuickCreateAppointmentModal from "@/components/QuickCreateAppointmentModal";
import SharedAppointmentModal from "@/components/SharedAppointmentModal";

export const WD_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const WD_MIN = ["L", "M", "M", "G", "V", "S", "D"];
export const MONTHS_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

function pad(n) { return String(n).padStart(2, "0"); }
export function dateKey(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
export function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
export function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const isoDow = (first.getDay() + 6) % 7;
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - isoDow);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  return cells;
}

/**
 * Normalizza il payload agenda in una lista piatta di eventi con `date`.
 * Ogni evento ha: {id, title, kind, property_id, property_name, creator_name,
 * start_at, duration_min, dateKey, dt, location_display, service_name, practice_id, readonly}
 */
export function flattenAgenda(payload) {
  const appts = payload?.appointments || [];
  const visits = payload?.site_visits || [];
  const events = [];
  const push = (a, kindOverride) => {
    const slotList = a.slots || [];
    let startIso = null;
    let duration = 60;
    let totalSlots = slotList.length;
    let isPending = false;
    if (a.confirmed_slot_id && slotList.length) {
      const s = slotList.find((x) => x.id === a.confirmed_slot_id) || slotList[0];
      startIso = s?.start_at;
      duration = s?.duration_min || 60;
    } else if (slotList.length === 1) {
      startIso = slotList[0].start_at;
      duration = slotList[0].duration_min || 60;
    } else if (slotList.length > 1) {
      // Iter208.61 — shared multi-slot ancora in attesa: mostra UNA sola card
      // ancorata al PRIMO slot proposto, con badge "IN ATTESA" e "+N slot".
      // In precedenza si generava un evento per ogni slot (calendario e lista
      // affollati).
      const sorted = [...slotList].sort((x, y) => new Date(x.start_at) - new Date(y.start_at));
      startIso = sorted[0].start_at;
      duration = sorted[0].duration_min || 60;
      isPending = true;
    }
    if (!startIso) return;
    const dt = new Date(startIso);
    if (Number.isNaN(dt.getTime())) return;
    events.push({
      id: a._id,
      appointment_id: a._id,
      title: a.title,
      kind: kindOverride || a.kind || "shared",
      property_id: a.property_id,
      property_name: a.property_name,
      creator_name: a.creator_name,
      practice_id: a.practice_id || null,
      service_name: a.service_name || null,
      location_display: a.location_display || "",
      public_url: a.public_url || null,
      status: a.status,
      readonly: a.readonly || false,
      start_at: startIso,
      duration_min: duration,
      dt,
      dateKey: dateKey(dt),
      // Iter208.61 — meta per il rendering delle card pending vs confirmed
      is_pending_multi: isPending,
      total_slots: totalSlots,
      confirmed: !!a.confirmed_slot_id,
    });
  };
  appts.forEach((a) => push(a));
  visits.forEach((v) => push(v, "site_visit"));
  // Iter209e0 — Scadenze immobili (evento giornaliero, solo eliminabile).
  (payload?.deadlines || []).forEach((d) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d.date || "");
    if (!m) return;
    const dt = new Date(+m[1], +m[2] - 1, +m[3]);
    events.push({
      id: d._id,
      title: d.title,
      description: d.description || "",
      kind: "deadline",
      property_id: d.property_id,
      property_name: d.property_name,
      location_display: d.location_display || "",
      all_day: true,
      start_at: dt.toISOString(),
      duration_min: 0,
      dt,
      dateKey: dateKey(dt),
    });
  });
  return events;
}

// Iter209e0 — Etichetta orario (le scadenze sono "tutto il giorno").
export function timeLabel(e, allDayText = "Giornata") {
  if (e.all_day) return allDayText;
  return e.dt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}

export function kindMeta(kind) {
  if (kind === "deadline") return { label: "Scadenza", color: "bg-red-100 text-red-900 border-red-300", dot: "bg-red-500", icon: BellRinging };
  if (kind === "site_visit") return { label: "Sopralluogo", color: "bg-blue-100 text-blue-900 border-blue-300", dot: "bg-blue-500", icon: Wrench };
  if (kind === "personal") return { label: "Personale", color: "bg-purple-100 text-purple-900 border-purple-300", dot: "bg-purple-500", icon: User };
  return { label: "Condiviso", color: "bg-emerald-100 text-emerald-900 border-emerald-300", dot: "bg-emerald-500", icon: CalendarBlank };
}

export default function ProCalendarPage() {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDayKey, setSelectedDayKey] = useState(() => dateKey(new Date()));
  const [properties, setProperties] = useState([]);
  // Flow "+ Nuovo": prima si sceglie l'immobile, poi il tipo, poi si va al form nell'immobile
  const [pickPropOpen, setPickPropOpen] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [pickedPropId, setPickedPropId] = useState(null);
  // Iter208.49 — Quick create inline (personale) con prefill data grid
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  // Iter208.53 — Shared appointment inline (con property picker dentro il modal)
  const [sharedCreateOpen, setSharedCreateOpen] = useState(false);
  // Iter208.51 — Toggle Lista/Calendario (stesso pattern di AppointmentsSection).
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("ht_pro_calendar_view") || "calendar"; } catch { return "calendar"; }
  });
  const changeView = (v) => {
    setViewMode(v);
    try { localStorage.setItem("ht_pro_calendar_view", v); } catch (_) { /* no-op */ }
  };

  // Iter208.62 — Detail modal + edit modal state
  const [detailEvent, setDetailEvent] = useState(null); // evento aperto in dettaglio
  const [editEvent, setEditEvent] = useState(null); // evento aperto in edit
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/me/agenda");
      setEvents(flattenAgenda(r.data || {}));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento agenda");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Fetch immobili per il flow "+ Nuovo"
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/properties", { params: { archived: false } });
        setProperties(r.data || []);
      } catch { setProperties([]); }
    })();
  }, []);

  const eventsByDay = useMemo(() => {
    const m = new Map();
    for (const e of events) {
      if (!m.has(e.dateKey)) m.set(e.dateKey, []);
      m.get(e.dateKey).push(e);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.dt - b.dt);
    return m;
  }, [events]);

  const monthCells = useMemo(() => buildMonthGrid(anchor.getFullYear(), anchor.getMonth()), [anchor]);
  const today = new Date();
  const monthLabel = `${MONTHS_IT[anchor.getMonth()]} ${anchor.getFullYear()}`;

  const goPrev = () => setAnchor((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const goNext = () => setAnchor((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const goToday = () => {
    const t = new Date();
    setAnchor(new Date(t.getFullYear(), t.getMonth(), 1));
    setSelectedDayKey(dateKey(t));
  };

  const openEvent = (e) => {
    if (e.kind === "site_visit" && e.practice_id) {
      // Read-only: naviga alla pratica per vedere il sopralluogo
      navigate(`/practices/${e.practice_id}`);
      return;
    }
    // Iter208.62 — Apri detail modal invece di navigare all'immobile
    setDetailEvent(e);
  };

  // Iter208.62 — Modifica: apre il modal giusto (personal vs shared) in edit
  const openEdit = async (e) => {
    setDetailEvent(null);
    try {
      const r = await api.get(`/properties/${e.property_id}/appointments`);
      const full = (r.data || []).find((x) => x._id === e.id);
      if (!full) {
        toast.error("Appuntamento non trovato");
        return;
      }
      setEditEvent({ kind: e.kind, appointment: full });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento dettagli");
    }
  };

  // Iter208.62 — Eliminazione con conferma
  const handleDelete = async (e) => {
    const msg = e.kind === "deadline"
      ? `Eliminare "${e.title}" dal calendario? La scadenza resta sull'immobile.`
      : `Eliminare l'appuntamento "${e.title}"? L'operazione è irreversibile.`;
    if (!confirm(msg)) return;
    setDeletingId(e.id);
    try {
      await api.delete(e.kind === "deadline" ? `/me/agenda/deadlines/${e.id}` : `/appointments/${e.id}`);
      toast.success(e.kind === "deadline" ? "Scadenza rimossa dal calendario" : "Appuntamento eliminato");
      setDetailEvent(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    } finally {
      setDeletingId(null);
    }
  };

  // Iter208.62 — WhatsApp share (solo shared con public_url disponibile)
  const buildWhatsAppHref = (e) => {
    const link = e.public_url || "";
    const dateStr = e.dt.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
    const timeStr = e.dt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    const text = link
      ? `Ciao! Ti invito all'appuntamento "${e.title}" del ${dateStr} alle ${timeStr}. Scegli lo slot che preferisci qui: ${link}`
      : `Ciao! Ti invito all'appuntamento "${e.title}" del ${dateStr} alle ${timeStr}.`;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  };

  // Iter208.62 — È una data passata?
  const isPastDate = (d) => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return d < start;
  };

  const startCreate = () => {
    if (properties.length === 0) {
      toast.info("Aggiungi prima almeno un immobile per creare un appuntamento");
      return;
    }
    // Iter208.45 — Prima chiede il tipo (Personale/Condiviso), poi (solo per
    // Condiviso) mostra il selettore immobile. Per Personale skippa il picker
    // e usa il primo immobile in silenzio (il form personale non richiede
    // input dall'utente su questo aspetto: crea comunque l'evento privato).
    setChooserOpen(true);
  };

  const onPickProperty = (pid) => {
    setPickedPropId(pid);
    setPickPropOpen(false);
    // Ora che l'utente ha scelto l'immobile per il tipo Condiviso, naviga
    navigate(`/properties/${pid}?tab=appointments&new=1`);
  };

  const onChooseKind = (kind) => {
    setChooserOpen(false);
    if (kind === "personal") {
      // Iter208.49 — Personale: apre inline il quick modal con date prefill.
      if (properties.length === 0) {
        toast.info("Aggiungi prima almeno un immobile");
        return;
      }
      setQuickCreateOpen(true);
    } else {
      // Iter208.53 — Condiviso: apre inline il modal condiviso con property
      // picker integrato. Niente più navigate a /properties/{id}.
      if (properties.length === 0) {
        toast.info("Aggiungi prima almeno un immobile");
        return;
      }
      setSharedCreateOpen(true);
    }
  };

  const daySlots = eventsByDay.get(selectedDayKey) || [];
  const anySelectedDate = new Date(`${selectedDayKey}T00:00:00`);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="pro-calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-display font-semibold text-slate-900 flex items-center gap-2">
            <CalendarBlank size={24} weight="fill" className="text-purple-600" />
            Calendario
          </h1>
          <p className="text-sm text-slate-500">
            Tutti gli appuntamenti e sopralluoghi dei tuoi immobili.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Iter208.51 — Toggle Lista/Calendario */}
          <div className="inline-flex rounded-md border border-slate-200 bg-white overflow-hidden" data-testid="pro-calendar-view-toggle">
            <button
              type="button"
              onClick={() => changeView("list")}
              className={
                "px-2.5 py-1.5 text-[11px] font-semibold inline-flex items-center gap-1 " +
                (viewMode === "list"
                  ? "bg-purple-600 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50")
              }
              data-testid="pro-calendar-view-list-btn"
              aria-pressed={viewMode === "list"}
              title="Vista Lista"
            >
              <ListIcon size={13} weight="bold" />
              <span className="hidden sm:inline">Lista</span>
            </button>
            <button
              type="button"
              onClick={() => changeView("calendar")}
              className={
                "px-2.5 py-1.5 text-[11px] font-semibold inline-flex items-center gap-1 border-l border-slate-200 " +
                (viewMode === "calendar"
                  ? "bg-purple-600 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50")
              }
              data-testid="pro-calendar-view-calendar-btn"
              aria-pressed={viewMode === "calendar"}
              title="Vista Calendario"
            >
              <CalendarBlank size={13} weight="bold" />
              <span className="hidden sm:inline">Calendario</span>
            </button>
          </div>
          {/* Iter208.52 — Il "+ Nuovo" fisso è visibile solo in vista Lista.
              Nella vista Calendario il tasto compare in overlay sul giorno selezionato. */}
          {viewMode === "list" && (
            <Button
              onClick={startCreate}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="pro-calendar-new"
            >
              <Plus size={16} weight="bold" className="mr-1" />
              Nuovo
            </Button>
          )}
        </div>
      </div>

      {/* Toolbar navigazione — visibile solo in vista Calendario */}
      {viewMode === "calendar" && (
      <>
      <div className="kpi-card">
        <div className="flex items-center justify-between border-b pb-2 mb-3">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={goPrev}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-700"
              aria-label="Mese precedente"
              data-testid="pro-cal-prev"
            >
              <CaretLeft size={16} weight="bold" />
            </button>
            <button
              type="button"
              onClick={goNext}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-700"
              aria-label="Mese successivo"
              data-testid="pro-cal-next"
            >
              <CaretRight size={16} weight="bold" />
            </button>
            <button
              type="button"
              onClick={goToday}
              className="ml-1 text-xs font-semibold px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-100 text-slate-700"
              data-testid="pro-cal-today"
            >
              Oggi
            </button>
          </div>
          <div className="font-semibold text-slate-900 flex items-center gap-2 capitalize" data-testid="pro-cal-month">
            {monthLabel}
          </div>
          <div className="w-[92px]" />
        </div>

        {/* Weekday header */}
        <div className="grid grid-cols-7 text-center text-[10px] sm:text-[11px] font-semibold text-slate-500 uppercase border-b">
          {WD_SHORT.map((w, i) => (
            <div key={w} className="py-1.5">
              <span className="hidden sm:inline">{w}</span>
              <span className="sm:hidden">{WD_MIN[i]}</span>
            </div>
          ))}
        </div>

        {/* Griglia 7x6 */}
        {loading ? (
          <div className="text-center py-16 text-sm text-slate-500">Caricamento…</div>
        ) : (
          <div className="grid grid-cols-7 grid-rows-6 auto-rows-fr border-b" data-testid="pro-cal-grid">
            {monthCells.map((d, idx) => {
              const inMonth = d.getMonth() === anchor.getMonth();
              const isToday = sameDay(d, today);
              const past = isPastDate(d);
              const k = dateKey(d);
              const list = eventsByDay.get(k) || [];
              const hasEvents = list.length > 0;
              const isSelected = selectedDayKey === k;
              return (
                <div
                  role="button"
                  tabIndex={past ? -1 : 0}
                  key={idx}
                  onClick={() => { if (!past) setSelectedDayKey(k); }}
                  onKeyDown={(ev) => { if (!past && (ev.key === "Enter" || ev.key === " ")) { ev.preventDefault(); setSelectedDayKey(k); } }}
                  className={
                    "relative min-h-[70px] sm:min-h-[92px] border-r border-b last:border-r-0 p-1 flex flex-col items-start justify-start text-left transition-colors outline-none " +
                    (!inMonth
                      ? "bg-slate-50/70 text-slate-300 cursor-default "
                      : past
                        ? "bg-slate-50 text-slate-400 cursor-default "
                        : "bg-white hover:bg-purple-50 text-slate-800 cursor-pointer ") +
                    (isSelected && inMonth && !past ? "ring-2 ring-purple-500 ring-inset bg-purple-50/60 " : "")
                  }
                  data-testid={`pro-cal-cell-${k}`}
                  data-has-events={hasEvents ? "1" : "0"}
                  data-past={past ? "1" : "0"}
                  aria-disabled={past ? "true" : "false"}
                >
                  <span
                    className={
                      "text-[11px] sm:text-xs font-semibold self-end mr-1 " +
                      (isToday && inMonth
                        ? "inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-purple-600 text-white"
                        : "")
                    }
                  >
                    {d.getDate()}
                  </span>
                  {inMonth && (
                    <div className="mt-1 w-full space-y-0.5 overflow-hidden">
                      {list.slice(0, 3).map((e) => {
                        const meta = kindMeta(e.kind);
                        const timeStr = e.all_day ? "" : timeLabel(e);
                        // Iter208.61 — sfumatura pending su calendar cell
                        const pending = e.is_pending_multi;
                        const cellCls = pending
                          ? "w-full truncate text-[10px] leading-tight px-1 py-0.5 rounded border bg-amber-50 border-amber-200 text-amber-800 hover:brightness-95 cursor-pointer"
                          : "w-full truncate text-[10px] leading-tight px-1 py-0.5 rounded border hover:brightness-95 cursor-pointer " + meta.color;
                        return (
                          <button
                            type="button"
                            key={e.id}
                            onClick={(ev) => { ev.stopPropagation(); openEvent(e); }}
                            className={cellCls}
                            data-testid={`pro-cal-cell-event-${e.id}`}
                            title={`${e.title} — ${e.property_name || ""}${pending ? ` · ${e.total_slots} slot proposti in attesa` : ""}`}
                          >
                            <span className={"inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle " + (pending ? "bg-amber-500" : meta.dot)} />
                            {timeStr} {e.title}
                            {pending && e.total_slots > 1 && (
                              <span className="ml-1 text-[9px] font-bold">+{e.total_slots - 1}</span>
                            )}
                          </button>
                        );
                      })}
                      {list.length > 3 && (
                        <div className="text-[9px] font-bold text-slate-500 pl-1">
                          +{list.length - 3} altri
                        </div>
                      )}
                    </div>
                  )}
                  {/* Iter208.52 — Bottone elevato "+ Nuovo" solo sul giorno selezionato non passato */}
                  {isSelected && inMonth && !past && (
                    <button
                      type="button"
                      onClick={(ev) => { ev.stopPropagation(); startCreate(); }}
                      className="absolute -top-2 right-1 bg-purple-600 hover:bg-purple-700 text-white text-[10px] font-bold rounded-full shadow-lg px-2 py-1 inline-flex items-center gap-1 border border-white ring-1 ring-purple-200 transition-transform hover:scale-105 z-10"
                      data-testid={`pro-cal-cell-new-${k}`}
                      title="Crea appuntamento in questo giorno"
                    >
                      <Plus size={11} weight="bold" />
                      <span>Nuovo</span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Dettaglio eventi del giorno selezionato */}
      <div className="kpi-card mt-4" data-testid="pro-cal-day-detail">
        <h3 className="text-sm font-semibold text-slate-800 mb-3 capitalize">
          {anySelectedDate.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          <span className="ml-2 text-xs font-normal text-slate-500">
            ({daySlots.length} eventi)
          </span>
        </h3>
        {daySlots.length === 0 ? (
          <div className="text-center text-sm text-slate-500 italic py-6">
            Nessun evento in questo giorno.
          </div>
        ) : (
          <ul className="divide-y">
            {daySlots.map((e) => {
              const meta = kindMeta(e.kind);
              const Icon = meta.icon;
              return (
                <li
                  key={e.id}
                  className="py-3 flex items-start justify-between gap-3 hover:bg-slate-50 -mx-2 px-2 rounded cursor-pointer transition-colors"
                  onClick={() => openEvent(e)}
                  data-testid={`pro-cal-event-${e.id}`}
                >
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={"shrink-0 w-9 h-9 rounded-lg flex items-center justify-center border " + meta.color}>
                      <Icon size={16} weight="bold" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900 truncate">{e.title}</span>
                        <span className={"inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase " + meta.color}>
                          {meta.label}
                        </span>
                        {/* Iter208.61 — Badge stato per shared multi-slot */}
                        {e.kind === "shared" && (
                          e.confirmed ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="appt-badge-confirmed">
                              CONFERMATO
                            </span>
                          ) : e.is_pending_multi ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase bg-amber-100 text-amber-800 border border-amber-200" data-testid="appt-badge-pending">
                              IN ATTESA
                            </span>
                          ) : null
                        )}
                        {e.readonly && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase bg-slate-100 text-slate-600">
                            Read-only
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-slate-600 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="inline-flex items-center gap-1">
                          <Clock size={12} className="text-slate-400" />
                          {timeLabel(e, "Tutto il giorno")}
                          {e.duration_min ? ` (${e.duration_min} min)` : ""}
                          {e.is_pending_multi && e.total_slots > 1 && (
                            <span className="ml-1 font-semibold text-amber-700">· + {e.total_slots - 1} slot</span>
                          )}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Buildings size={12} className="text-slate-400" />
                          {e.property_name}
                        </span>
                        {e.creator_name && e.creator_name !== "—" && (
                          <span className="inline-flex items-center gap-1">
                            <User size={12} className="text-slate-400" />
                            {e.creator_name}
                          </span>
                        )}
                        {e.location_display && (
                          <span className="inline-flex items-center gap-1 truncate">
                            <MapPin size={12} className="text-slate-400" />
                            <span className="truncate max-w-[220px]">{e.location_display}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Iter208.62 — Icone azione (WhatsApp, Edit, Delete) */}
                  <EventActions
                    e={e}
                    onEdit={() => openEdit(e)}
                    onDelete={() => handleDelete(e)}
                    onWhatsApp={() => window.open(buildWhatsAppHref(e), "_blank", "noopener,noreferrer")}
                    disabled={deletingId === e.id}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>
      </>
      )}

      {/* Iter208.51 — Vista Lista cronologica */}
      {viewMode === "list" && (
        <div className="kpi-card" data-testid="pro-cal-list-view">
          {loading ? (
            <div className="text-center py-16 text-sm text-slate-500">Caricamento…</div>
          ) : events.length === 0 ? (
            <div className="text-center text-sm text-slate-500 italic py-8">
              Nessun appuntamento in agenda.
            </div>
          ) : (
            <ul className="divide-y">
              {[...events]
                .sort((a, b) => a.dt - b.dt)
                .map((e) => {
                  const meta = kindMeta(e.kind);
                  const Icon = meta.icon;
                  const dateStr = e.dt.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
                  const timeStr = timeLabel(e);
                  return (
                    <li
                      key={e.id}
                      className="py-3 flex items-start justify-between gap-3 hover:bg-slate-50 -mx-2 px-2 rounded cursor-pointer transition-colors"
                      onClick={() => openEvent(e)}
                      data-testid={`pro-cal-list-item-${e.id}`}
                    >
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="flex flex-col items-center shrink-0 w-16 text-center">
                          <div className="text-[10px] uppercase font-semibold tracking-wide text-slate-500 leading-tight">
                            {dateStr}
                          </div>
                          <div className="text-lg font-display font-bold text-purple-700 leading-none mt-0.5">
                            {timeStr}
                          </div>
                        </div>
                        <div className={"shrink-0 w-9 h-9 rounded-lg flex items-center justify-center border " + meta.color}>
                          <Icon size={16} weight="bold" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-slate-900 truncate">{e.title}</span>
                            <span className={"inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase " + meta.color}>
                              {meta.label}
                            </span>
                            {/* Iter208.61 — Badge stato shared */}
                            {e.kind === "shared" && (
                              e.confirmed ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="list-badge-confirmed">
                                  CONFERMATO
                                </span>
                              ) : e.is_pending_multi ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase bg-amber-100 text-amber-800 border border-amber-200" data-testid="list-badge-pending">
                                  IN ATTESA
                                </span>
                              ) : null
                            )}
                            {e.readonly && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase bg-slate-100 text-slate-600">
                                Read-only
                              </span>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-slate-600 flex flex-wrap items-center gap-x-3 gap-y-1">
                            {e.duration_min ? (
                              <span className="inline-flex items-center gap-1">
                                <Clock size={12} className="text-slate-400" />
                                {e.duration_min} min
                                {e.is_pending_multi && e.total_slots > 1 && (
                                  <span className="ml-1 font-semibold text-amber-700">· + {e.total_slots - 1} slot</span>
                                )}
                              </span>
                            ) : null}
                            {e.kind !== "personal" && e.property_name && (
                              <span className="inline-flex items-center gap-1">
                                <Buildings size={12} className="text-slate-400" />
                                {e.property_name}
                              </span>
                            )}
                            {e.creator_name && e.creator_name !== "—" && (
                              <span className="inline-flex items-center gap-1">
                                <User size={12} className="text-slate-400" />
                                {e.creator_name}
                              </span>
                            )}
                            {e.location_display && (
                              <span className="inline-flex items-center gap-1 truncate">
                                <MapPin size={12} className="text-slate-400" />
                                <span className="truncate max-w-[220px]">{e.location_display}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Iter208.62 — Icone azione (WhatsApp, Edit, Delete) */}
                      <EventActions
                        e={e}
                        onEdit={() => openEdit(e)}
                        onDelete={() => handleDelete(e)}
                        onWhatsApp={() => window.open(buildWhatsAppHref(e), "_blank", "noopener,noreferrer")}
                        disabled={deletingId === e.id}
                      />
                    </li>
                  );
                })}
            </ul>
          )}
        </div>
      )}

      {/* Property picker modal */}
      <Dialog open={pickPropOpen} onOpenChange={(v) => !v && setPickPropOpen(false)}>
        <DialogContent className="max-w-md" data-testid="pro-cal-pick-property">
          <DialogHeader>
            <DialogTitle>Nuovo appuntamento</DialogTitle>
            <DialogDescription>
              Seleziona l'immobile per cui creare l'appuntamento.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto divide-y">
            {properties.length === 0 && (
              <div className="text-center text-sm text-slate-500 py-6">
                Nessun immobile disponibile.
              </div>
            )}
            {properties.map((p) => (
              <button
                key={p._id}
                type="button"
                onClick={() => onPickProperty(p._id)}
                className="w-full text-left px-2 py-3 hover:bg-purple-50 flex items-center gap-2 transition-colors"
                data-testid={`pro-cal-pick-prop-${p._id}`}
              >
                <Buildings size={16} className="text-purple-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-slate-900 truncate">
                    {p.title || p.address || "Immobile"}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {p.city || p.address || ""}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Kind chooser (personal / shared) */}
      <AppointmentTypeChooserModal
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        onPick={onChooseKind}
      />

      {/* Iter208.49 — Quick create inline con prefill data dal calendario */}
      <QuickCreateAppointmentModal
        open={quickCreateOpen}
        onClose={() => setQuickCreateOpen(false)}
        properties={properties}
        initialDate={new Date(`${selectedDayKey}T10:00:00`)}
        onCreated={() => { setQuickCreateOpen(false); load(); }}
        hidePropertyPicker={true}
      />

      {/* Iter208.53 — Shared appointment inline (property picker nel modal) */}
      <SharedAppointmentModal
        open={sharedCreateOpen}
        onClose={() => setSharedCreateOpen(false)}
        properties={properties}
        initialDate={new Date(`${selectedDayKey}T10:00:00`)}
        onCreated={() => { load(); }}
      />

      {/* Iter208.62 — Detail modal */}
      <AppointmentDetailModal
        event={detailEvent}
        onClose={() => setDetailEvent(null)}
        onEdit={() => detailEvent && openEdit(detailEvent)}
        onDelete={() => detailEvent && handleDelete(detailEvent)}
        onWhatsApp={() => detailEvent && window.open(buildWhatsAppHref(detailEvent), "_blank", "noopener,noreferrer")}
        onOpenProperty={() => detailEvent?.property_id && navigate(`/properties/${detailEvent.property_id}`)}
      />

      {/* Iter208.62 — Edit modal (Personal → QuickCreate, Shared → Shared modal) */}
      {editEvent?.kind === "personal" && (
        <QuickCreateAppointmentModal
          open={!!editEvent}
          onClose={() => setEditEvent(null)}
          propertyId={editEvent.appointment.property_id}
          properties={properties}
          editAppointment={editEvent.appointment}
          onCreated={() => { setEditEvent(null); load(); }}
          hidePropertyPicker={true}
        />
      )}
      {editEvent?.kind === "shared" && (
        <SharedAppointmentModal
          open={!!editEvent}
          onClose={() => setEditEvent(null)}
          properties={properties}
          editAppointment={editEvent.appointment}
          onCreated={() => { setEditEvent(null); load(); }}
        />
      )}
    </div>
  );
}

// ==================== Iter208.62 — EventActions ====================
// Icone inline (WhatsApp/Edit/Delete) mostrate a destra su ogni card evento.
// WhatsApp visibile solo per appuntamenti CONDIVISI (con `public_url`).
// Edit/Delete non compaiono su eventi read-only (site_visit dalle pratiche).
function EventActions({ e, onEdit, onDelete, onWhatsApp, disabled }) {
  if (e.readonly || e.kind === "site_visit") {
    return <ArrowRight size={14} className="text-slate-400 mt-2 shrink-0" />;
  }
  const isShared = e.kind === "shared";
  return (
    <div className="flex items-center gap-1 shrink-0 mt-1" data-testid={`pro-cal-actions-${e.id}`}>
      {isShared && (
        <button
          type="button"
          onClick={(ev) => { ev.stopPropagation(); onWhatsApp?.(); }}
          className="w-8 h-8 inline-flex items-center justify-center rounded-md text-[#25D366] hover:bg-emerald-50 transition-colors"
          title="Condividi su WhatsApp"
          aria-label="Condividi su WhatsApp"
          data-testid={`pro-cal-action-whatsapp-${e.id}`}
        >
          <WhatsappLogo size={18} weight="fill" />
        </button>
      )}
      {e.kind !== "deadline" && <button
        type="button"
        onClick={(ev) => { ev.stopPropagation(); onEdit?.(); }}
        className="w-8 h-8 inline-flex items-center justify-center rounded-md text-slate-500 hover:bg-purple-50 hover:text-purple-700 transition-colors"
        title="Modifica"
        aria-label="Modifica appuntamento"
        data-testid={`pro-cal-action-edit-${e.id}`}
      >
        <PencilSimple size={16} weight="bold" />
      </button>}
      <button
        type="button"
        onClick={(ev) => { ev.stopPropagation(); onDelete?.(); }}
        disabled={disabled}
        className="w-8 h-8 inline-flex items-center justify-center rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-50"
        title="Elimina"
        aria-label="Elimina appuntamento"
        data-testid={`pro-cal-action-delete-${e.id}`}
      >
        <Trash size={16} weight="bold" />
      </button>
    </div>
  );
}

// ==================== Iter208.62 — AppointmentDetailModal ====================
function AppointmentDetailModal({ event, onClose, onEdit, onDelete, onWhatsApp, onOpenProperty }) {
  if (!event) return null;
  const meta = kindMeta(event.kind);
  const Icon = meta.icon;
  const dateStr = event.dt.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const timeStr = timeLabel(event, "Tutto il giorno");
  const isShared = event.kind === "shared";
  const isDeadline = event.kind === "deadline";
  return (
    <Dialog open={!!event} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="pro-cal-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className={"inline-flex w-8 h-8 rounded-lg items-center justify-center border " + meta.color}>
              <Icon size={16} weight="bold" />
            </span>
            {isDeadline ? "Scadenza immobile" : "Dettaglio appuntamento"}
          </DialogTitle>
          <DialogDescription>
            <span className={"inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase " + meta.color}>
              {meta.label}
            </span>
            {isShared && (
              event.confirmed ? (
                <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase bg-emerald-100 text-emerald-800 border border-emerald-200">
                  CONFERMATO
                </span>
              ) : event.is_pending_multi ? (
                <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase bg-amber-100 text-amber-800 border border-amber-200">
                  IN ATTESA
                </span>
              ) : null
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <div className="font-semibold text-slate-900 text-base">{event.title}</div>
          {event.description && (
            <div className="text-slate-700 whitespace-pre-line" data-testid="pro-cal-detail-description">{event.description}</div>
          )}
          <div className="text-slate-700">
            <Clock size={14} className="inline mr-1 text-slate-400" />
            <span className="capitalize">{dateStr}</span> — {timeStr}
            {event.duration_min ? ` (${event.duration_min} min)` : ""}
          </div>
          {event.property_name && (
            <div className="text-slate-700" data-testid="pro-cal-detail-property">
              <Buildings size={14} className="inline mr-1 text-slate-400" />
              {event.property_name}
              {isDeadline && event.location_display ? ` · ${event.location_display}` : ""}
            </div>
          )}
          {event.location_display && !isDeadline && (
            <div className="text-slate-700">
              <MapPin size={14} className="inline mr-1 text-slate-400" />
              {event.location_display}
            </div>
          )}
          {event.creator_name && event.creator_name !== "—" && (
            <div className="text-slate-700">
              <User size={14} className="inline mr-1 text-slate-400" />
              {event.creator_name}
            </div>
          )}
          {isShared && event.is_pending_multi && event.total_slots > 1 && (
            <div className="text-amber-800 text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1">
              + {event.total_slots - 1} altri slot proposti in attesa di conferma.
            </div>
          )}
        </div>
        <div className="flex flex-wrap justify-end gap-2 pt-3 border-t mt-3">
          {isDeadline && event.property_id && (
            <Button
              variant="outline"
              onClick={onOpenProperty}
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="pro-cal-detail-open-property"
            >
              <Buildings size={16} className="mr-1" />
              Vai a immobile
            </Button>
          )}
          {isShared && (
            <Button
              variant="outline"
              onClick={onWhatsApp}
              className="border-[#25D366] text-[#25D366] hover:bg-emerald-50"
              data-testid="pro-cal-detail-whatsapp"
            >
              <WhatsappLogo size={16} weight="fill" className="mr-1" />
              WhatsApp
            </Button>
          )}
          <Button
            variant="outline"
            onClick={onDelete}
            className="border-red-300 text-red-600 hover:bg-red-50"
            data-testid="pro-cal-detail-delete"
          >
            <Trash size={16} weight="bold" className="mr-1" />
            Elimina
          </Button>
          {!isDeadline && (
            <Button
              onClick={onEdit}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="pro-cal-detail-edit"
            >
              <PencilSimple size={16} weight="bold" className="mr-1" />
              Modifica
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
